import { PERMISSION_KEY_PATTERN } from "./permission-key.js";
import { REASON_SUBSTANCE } from "./fixture-public-route-rules.js";
import { z } from "zod";
//#region src/route-inventory.ts
/** Mirrors `schemaVersion` in `route-inventory.schema.json`. Bump both together. */
const ROUTE_INVENTORY_SCHEMA_VERSION = 1;
/**
* Set to `1`/`true`/`yes`/`on` to make an SDK-built service print its route inventory to stdout
* and exit 0 before it listens. The CLI sets it when it runs a service image at release; nothing
* sets it in a deployed pod.
*/
const ROUTE_INVENTORY_ENV = "ZION_PRINT_ROUTE_INVENTORY";
/**
* Set to `1`/`true`/`yes`/`on` to make the pod's boot check refuse to serve on an undeclared
* route instead of logging it. Unset in release 1 (observe). Parsed by {@link parseEnforceFlag}.
*/
const ENFORCE_ROUTE_DECLARATIONS_ENV = "ZION_ENFORCE_ROUTE_DECLARATIONS";
/**
* The env var that names the service a fixture image is running as. The operator stamps it on
* every fixture pod (`GUARANTEED_INJECTED_ENV_NAMES` in `@zion/sdk-cp-dp-contracts`), and the
* CLI sets it when it runs one image per service under {@link ROUTE_INVENTORY_ENV}; the printer
* copies it into `RouteInventory.service`. Declared here so the printer and the CLI share one
* spelling without the CLI depending on the CP-DP package.
*/
const SERVICE_NAME_ENV = "ZION_SERVICE_NAME";
/**
* The prefix of the one stdout line that carries the inventory (ruling R-16). A framework logs to
* stdout while it boots, so the reader cannot treat the whole stream as the document. The SDK
* writes `ROUTE_INVENTORY_STDOUT_PREFIX + JSON.stringify(inventory) + '\n'` as one line and
* exits 0; the reader takes the last line that starts with the prefix and parses the remainder.
*/
const ROUTE_INVENTORY_STDOUT_PREFIX = "ZION_ROUTE_INVENTORY:";
/** The OCI layer mediaType `zion-app release` uses for a fixture's route inventory (R-7). */
const ROUTE_INVENTORY_LAYER_MEDIA_TYPE = "application/vnd.zion.fixture.route-inventory.v1+json";
/** The OCI layer filename for a fixture's route inventory: `<fixtureId>/route-inventory.json`. */
function routeInventoryLayerFilename(fixtureId) {
	return `${fixtureId}/route-inventory.json`;
}
/**
* The reserved caller token of a library route (shared-libraries ruling R-035). A library names
* no consuming app when it publishes, so each internal route of a library declares
* `callers: ["$consumer"]`, in `fixture.json` and in `@InternalRoute`. The operator substitutes
* the consumer slug of each instance for the token when it renders the mesh allow rule (WS-8).
* An app slug never holds `$`, so the token never equals an app slug. The schemas admit it.
* `checkInternalCallersRegistered`, the CP publish rule, refuses it on every fixture until the
* library publish rule of WS-2 admits it on a library route alone (S1 WS-0-R15).
*/
const CONSUMER_CALLER_TOKEN = "$consumer";
/**
* One `callers` member: an app slug as `app-manifest.schema.json` spells it (lower case, no
* trailing hyphen), or exactly {@link CONSUMER_CALLER_TOKEN}. The members are at most 63
* characters. `fixture.schema.json` and `route-inventory.schema.json` carry the same pattern
* text, and the parity suites pin the two doors to one verdict.
*/
const INTERNAL_ROUTE_CALLER_PATTERN = /^(?:[a-z][a-z0-9-]*[a-z0-9]|\$consumer)$/;
const SLUG = /^[a-z][a-z0-9-]*$/;
const HTTP_METHODS = [
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE",
	"HEAD",
	"OPTIONS",
	"ALL"
];
const SEMVER = /^[0-9]+\.[0-9]+\.[0-9]+([-+][0-9A-Za-z.-]+)?$/;
const routeDeclarationSchema = z.strictObject({
	decorator: z.enum([
		"AnonymousRoute",
		"AuthenticatedInHandler",
		"Public"
	]),
	reason: z.string().min(30).max(512).regex(REASON_SUBSTANCE, "reason needs 30 non-whitespace characters")
});
const internalRouteDeclarationSchema = z.strictObject({
	auth: z.enum(["user-jwt", "service-token"]),
	callers: z.array(z.string().regex(INTERNAL_ROUTE_CALLER_PATTERN).max(63)).min(1).refine((c) => new Set(c).size === c.length, { message: "callers are unique" })
});
/**
* One route. The `kind` decides which optional block may be present, the same `if/then` the
* JSON Schema carries: `internal` requires `internal`; `user` and `oidc` forbid both blocks;
* `public` may carry `declaration` and the rule reports its absence (R-17); `oidc` requires
* `oidcPermission` and every other kind forbids it (ruling R-005 of 2026-10-11-app-oidc). The
* parser does not compare `oidcPermission` with `permissions`, because the JSON Schema cannot say
* that and the two doors must agree; `checkRouteInventory` reports the gap.
*/
const routeInventoryRouteSchema = z.strictObject({
	method: z.enum(HTTP_METHODS),
	path: z.string().max(512).regex(/^\//),
	kind: z.enum([
		"user",
		"public",
		"internal",
		"oidc"
	]),
	permissions: z.array(z.string().regex(PERMISSION_KEY_PATTERN)).max(32),
	declaration: routeDeclarationSchema.optional(),
	internal: internalRouteDeclarationSchema.optional(),
	oidcPermission: z.string().regex(PERMISSION_KEY_PATTERN).optional(),
	controller: z.string().max(128).optional(),
	handler: z.string().max(128).optional()
}).superRefine((route, ctx) => {
	if (route.kind === "internal" && route.internal === void 0) ctx.addIssue({
		code: "custom",
		path: ["internal"],
		message: "an internal route carries its internal block"
	});
	if ((route.kind === "user" || route.kind === "oidc") && (route.declaration !== void 0 || route.internal !== void 0)) ctx.addIssue({
		code: "custom",
		path: ["kind"],
		message: `${route.kind === "user" ? "a user" : "an oidc"} route carries neither a declaration nor an internal block`
	});
	if (route.kind === "oidc" && route.oidcPermission === void 0) ctx.addIssue({
		code: "custom",
		path: ["oidcPermission"],
		message: "an oidc route names its @AcceptOidc permission"
	});
	if (route.kind !== "oidc" && route.oidcPermission !== void 0) ctx.addIssue({
		code: "custom",
		path: ["oidcPermission"],
		message: `only an oidc route carries oidcPermission, and this route is ${route.kind}`
	});
	if (new Set(route.permissions).size !== route.permissions.length) ctx.addIssue({
		code: "custom",
		path: ["permissions"],
		message: "permission keys are unique"
	});
});
/**
* The longest `sdk.version` a document may carry. The semver pattern bounds the charset and this
* bounds the length: the control plane persists the string per service per fixture and re-serves
* it on a 200-row page, so an unbounded prerelease suffix was a 1 MiB write per fixture. 64 covers
* every version the SDKs print (`28.5.0`; a prerelease `0.2.0-alpha.1` is 13) and sits beside the file's
* slug caps of 63 (ruling R-4 of 2026-09-19-app-sdk-version-read-path). The JSON Schema twin
* carries the same `maxLength`.
*/
const SDK_VERSION_MAX_LENGTH = 64;
/** The `sdk` member of one service's inventory: one of two package names and a semver string. */
const routeInventorySdkSchema = z.strictObject({
	name: z.enum(["@zion/sdk-nestjs", "zion-sdk-runtime"]),
	version: z.string().regex(SEMVER).max(64)
});
/** What one service prints. Parse the SDK's stdout with this before anything reads it. */
const routeInventorySchema = z.strictObject({
	schemaVersion: z.literal(1),
	service: z.string().regex(SLUG).max(63),
	sdk: routeInventorySdkSchema,
	routes: z.array(routeInventoryRouteSchema).max(2e3)
});
/** What the bundle layer carries. Parse the layer blob with this at the CP door. */
const fixtureRouteInventorySchema = z.strictObject({
	schemaVersion: z.literal(1),
	fixtureId: z.string().regex(SLUG).max(63),
	services: z.array(routeInventorySchema).min(1).max(64)
});
/** One {@link ServiceSdk}: the same bounds `routeInventorySchema` puts on `service` and `sdk`. */
const serviceSdkSchema = z.strictObject({
	service: z.string().regex(SLUG).max(63),
	sdk: routeInventorySdkSchema
});
/** One {@link FixtureServiceSdk}: a {@link serviceSdkSchema} entry plus the fixture id. */
const fixtureServiceSdkSchema = serviceSdkSchema.extend({ fixtureId: z.string().regex(SLUG).max(63) });
/**
* What `release.app_compositions.fixture_sdks` holds when it holds anything. No array cap: each
* fixture contributes at most the 64 services `fixtureRouteInventorySchema` admits, and a fixture
* appears once, so the length is at most 64 times the bundle's fixture count.
*/
const fixtureServiceSdksSchema = z.array(fixtureServiceSdkSchema);
/**
* The one derivation from a fixture's validated inventory to the entries the control plane keeps:
* one {@link FixtureServiceSdk} per `services[]` entry, in document order, keyed by `fixtureId`.
*
* `fixtureId` is the caller's, and it is the id the publish door validated: the layer's
* `zion.fixture.id` annotation, which `readRouteInventoryLayers` checks against the app manifest's
* fixture keys. The document's own `fixtureId` member is not read here, because no door compares
* it to the annotation, so a layer annotated `api` can carry a document that says `ui`; keying by
* the document would put one fixture's entries on another fixture's deployments row (ruling R-1 of
* 2026-09-19-app-sdk-version-read-path). Pure. It reads only a document
* `fixtureRouteInventorySchema` has parsed, so every string it returns is bounded by that schema's
* regexes; a caller holding an `unknown` parses first. The schema admits no empty `services`, so
* the result is never empty for a parsed document.
*/
function fixtureServiceSdks(fixtureId, inventory) {
	return inventory.services.map((svc) => ({
		fixtureId,
		service: svc.service,
		sdk: {
			name: svc.sdk.name,
			version: svc.sdk.version
		}
	}));
}
/** Values of an enforcement flag that mean on. Compared lower-cased and trimmed. */
const FLAG_TRUE = /* @__PURE__ */ new Set([
	"1",
	"true",
	"yes",
	"on"
]);
/** Values that mean off. The empty string is here: `FOO=` in a manifest means not set. */
const FLAG_FALSE = /* @__PURE__ */ new Set([
	"",
	"0",
	"false",
	"no",
	"off"
]);
/** Raised when an enforcement flag holds a value this module will not guess at. */
var InvalidRouteFlagError = class extends Error {
	constructor(name, raw) {
		super(`${name}=${JSON.stringify(raw)} is not a value I will interpret. Use one of ${[...FLAG_TRUE].join(", ")} to turn it on, one of ${[...FLAG_FALSE].filter((v) => v !== "").join(", ")} (or unset it) to turn it off. Refusing rather than guessing: a guess in either direction is wrong in a way nobody can see.`);
		this.name = "InvalidRouteFlagError";
	}
};
/**
* Parse an on/off env flag strictly. Absent is `false`. `1/true/yes/on` is `true`,
* `''/0/false/no/off` is `false`, anything else throws {@link InvalidRouteFlagError} before the
* app serves. The same table `parseEnforcementFlag` in `@zion/sdk-nestjs` uses for the public-route
* check; a value that means one thing there means the same here.
*/
function parseEnforceFlag(raw, name = ENFORCE_ROUTE_DECLARATIONS_ENV) {
	if (raw === void 0) return false;
	const value = raw.trim().toLowerCase();
	if (FLAG_TRUE.has(value)) return true;
	if (FLAG_FALSE.has(value)) return false;
	throw new InvalidRouteFlagError(name, raw);
}
/**
* Parse a mode string strictly: `observe` or `enforce`, trimmed and lower-cased. Absent or empty
* (a chart value that rendered nothing, `FOO=` in a manifest) returns `fallback`, the same
* reading {@link parseEnforceFlag} gives the empty string; anything else throws
* {@link InvalidRouteFlagError}.
*/
function parseRouteRuleMode(raw, fallback, name) {
	if (raw === void 0) return fallback;
	const value = raw.trim().toLowerCase();
	if (value === "") return fallback;
	if (value === "observe" || value === "enforce") return value;
	throw new InvalidRouteFlagError(name, raw);
}
//#endregion
export { CONSUMER_CALLER_TOKEN, ENFORCE_ROUTE_DECLARATIONS_ENV, INTERNAL_ROUTE_CALLER_PATTERN, InvalidRouteFlagError, ROUTE_INVENTORY_ENV, ROUTE_INVENTORY_LAYER_MEDIA_TYPE, ROUTE_INVENTORY_SCHEMA_VERSION, ROUTE_INVENTORY_STDOUT_PREFIX, SDK_VERSION_MAX_LENGTH, SERVICE_NAME_ENV, fixtureRouteInventorySchema, fixtureServiceSdkSchema, fixtureServiceSdks, fixtureServiceSdksSchema, parseEnforceFlag, parseRouteRuleMode, routeInventoryLayerFilename, routeInventoryRouteSchema, routeInventorySchema, routeInventorySdkSchema, serviceSdkSchema };

//# sourceMappingURL=route-inventory.js.map